import { toast } from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageWithSize } from '@/components/canvas/page/createPage';
import { parseCanvasData } from '@/components/canvas/document/validation';
import {
  canvasDataFromParsed,
  toCanvasData,
} from '@/components/canvas/document/serializeDocument';
import type { CanvasChangeMeta } from '@/components/canvas/designer/shell/types';
import type { CanvasData } from '@/components/canvas/document/publicTypes';
import { trpc } from '@/lib/trpc';

const SAVE_DEBOUNCE_MS = 500;
const DEFAULT_TITLE = '未命名';

export type KnowledgeBaseCanvasFileFormat = 'slides' | 'poster' | 'display';

export type UseKnowledgeBaseCanvasPersistenceOptions = {
  fileFormat: KnowledgeBaseCanvasFileFormat;
  /** 已有记录 id；新建模式为 undefined */
  recordId?: string;
  initialTitle?: string | null;
  initialContent?: unknown;
  /** 新建成功后替换为编辑路由（避免重复 create） */
  editPathAfterCreate?: (id: string) => string;
  /** 无 recordId 时用于冻结首屏文档（如从模板打开） */
  sessionKey?: string;
  enabled?: boolean;
};

export function useKnowledgeBaseCanvasPersistence({
  fileFormat,
  recordId: recordIdProp,
  initialTitle,
  initialContent,
  editPathAfterCreate,
  sessionKey,
  enabled = true,
}: UseKnowledgeBaseCanvasPersistenceOptions) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [title, setTitle] = useState(() => initialTitle?.trim() || DEFAULT_TITLE);
  const titleRef = useRef(title);
  titleRef.current = title;

  const effectiveIdRef = useRef<string | null>(recordIdProp ?? null);
  const [effectiveId, setEffectiveId] = useState<string | null>(recordIdProp ?? null);
  const latestDocumentRef = useRef<CanvasData | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const createStartedRef = useRef(false);

  useEffect(() => {
    effectiveIdRef.current = recordIdProp ?? null;
    setEffectiveId(recordIdProp ?? null);
    createStartedRef.current = false;
  }, [recordIdProp]);

  useEffect(() => {
    const t = initialTitle?.trim() || DEFAULT_TITLE;
    setTitle(t);
    titleRef.current = t;
  }, [initialTitle, recordIdProp]);

  const updateMutation = trpc.knowledgeBases.update.useMutation({
    onSuccess: (_data, variables) => {
      // 内容自动保存会携带大 JSON；勿每次 invalidate listMy（SELECT * + ORDER BY 易触发 ER_OUT_OF_SORTMEMORY）
      if (variables?.id) {
        void utils.knowledgeBases.get.invalidate({ id: variables.id });
      }
    },
  });

  const createMutation = trpc.knowledgeBases.create.useMutation({
    onSuccess: () => void utils.knowledgeBases.listMy.invalidate(),
  });
  const createMutateAsyncRef = useRef(createMutation.mutateAsync);
  createMutateAsyncRef.current = createMutation.mutateAsync;
  const updateMutateAsyncRef = useRef(updateMutation.mutateAsync);
  updateMutateAsyncRef.current = updateMutation.mutateAsync;

  const bootDocumentRef = useRef<CanvasData | null>(null);
  const bootKeyRef = useRef<string | null>(null);
  const freezeKey = recordIdProp ?? sessionKey ?? null;

  useEffect(() => {
    if (freezeKey !== bootKeyRef.current) {
      bootKeyRef.current = freezeKey;
      bootDocumentRef.current = null;
    }
  }, [freezeKey]);

  const initialDocument = useMemo((): CanvasData | null => {
    if (bootDocumentRef.current && bootKeyRef.current === freezeKey) {
      return bootDocumentRef.current;
    }
    if (!enabled || initialContent == null) return null;
    const parsed = parseCanvasData(initialContent);
    if (!parsed) return null;
    const doc = canvasDataFromParsed(parsed);
    if (freezeKey) {
      bootDocumentRef.current = doc;
      bootKeyRef.current = freezeKey;
    }
    return doc;
  }, [enabled, initialContent, freezeKey]);

  const flushSave = useCallback(async (documentOverride?: CanvasData): Promise<boolean> => {
    if (!enabled) return false;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (documentOverride) {
      latestDocumentRef.current = documentOverride;
    }
    const doc = latestDocumentRef.current;
    if (!doc) return false;
    if (savingRef.current) return false;

    savingRef.current = true;
    const payload = doc as unknown as Record<string, unknown>;
    const titleForServer = titleRef.current.trim() || DEFAULT_TITLE;

    try {
      let id = effectiveIdRef.current;
      if (!id) {
        if (createStartedRef.current) {
          savingRef.current = false;
          return false;
        }
        createStartedRef.current = true;
        const created = await createMutateAsyncRef.current({
          title: titleForServer,
          fileFormat,
          content: payload,
        });
        id = created.id ?? null;
        if (!id) {
          createStartedRef.current = false;
          toast.danger('创建失败');
          savingRef.current = false;
          return false;
        }
        effectiveIdRef.current = id;
        setEffectiveId(id);
        if (editPathAfterCreate) {
          navigate(editPathAfterCreate(id), { replace: true });
        }
      } else {
        await updateMutateAsyncRef.current({
          id,
          data: { title: titleForServer, content: payload },
        });
      }
      savingRef.current = false;
      return true;
    } catch {
      createStartedRef.current = false;
      savingRef.current = false;
      toast.danger(effectiveIdRef.current ? '保存失败' : '创建失败');
      return false;
    }
  }, [enabled, fileFormat, editPathAfterCreate, navigate]);

  const scheduleContentSave = useCallback(
    (document: CanvasData, meta: CanvasChangeMeta) => {
      if (!enabled || meta.reason !== 'edit') return;
      latestDocumentRef.current = document;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        void flushSave();
      }, SAVE_DEBOUNCE_MS);
    },
    [enabled, flushSave],
  );

  const flushTitleSave = useCallback(() => {
    const id = effectiveIdRef.current;
    if (!id || !enabled) return;
    const titleForServer = titleRef.current.trim() || DEFAULT_TITLE;
    updateMutation.mutate(
      { id, data: { title: titleForServer } },
      { onError: () => toast.danger('标题保存失败') },
    );
  }, [enabled, updateMutation]);

  const handleRename = useCallback(
    (name: string) => {
      const next = name.trim() || DEFAULT_TITLE;
      setTitle(next);
      titleRef.current = next;
      flushTitleSave();
    },
    [flushTitleSave],
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const seedNewDocument = useCallback((size: { width: number; height: number }) => {
    const page = createPageWithSize(size.width, size.height);
    const doc = toCanvasData([page], []);
    latestDocumentRef.current = doc;
    return doc;
  }, []);

  return {
    effectiveId,
    title,
    initialDocument,
    scheduleContentSave,
    flushSave,
    handleRename,
    seedNewDocument,
  };
}
